const { getElement } = window.BAO.Utils

let dropdownLoaded = false

class HeaderDropdown extends window.BAO.CustomElement() {
  connectedCallback () {
    this.isDesktopMode = window.BAO.MediaBreakpoints.isMd

    super.connectedCallback()

    if (dropdownLoaded) return

    if (!this.isDesktopMode) return

    window.BAO.sectionRenderer
      .fetch('header', `${window.Shopify.routes.root}?view=dropdown`)
      .then((res) => {
        dropdownLoaded = true

        const newDOM = window.BAO.sectionRenderer.convertToDOM(res.header)
        const myself = newDOM.querySelector(this.localName)

        this.outerHTML = myself.outerHTML
      })
      .catch(() => {})
  }

  setupListeners () {
    super.setupListeners()

    this.onViewportChange = this.onViewportChange.bind(this)
    this.listeners.add(
      window.matchMedia('(min-width: 901px)'),
      'change',
      this.onViewportChange,
    )

    if (!this.isDesktopMode) return

    this.initDesktopListeners()
  }

  initDesktopListeners () {
    this.handleToggleButtonClick = this.handleToggleButtonClick.bind(this)
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)
    this.handleFocusIn = this.handleFocusIn.bind(this)
    this.handleEscapePress = this.handleEscapePress.bind(this)

    if (this.els?.['menu-dropdown-toggle']?.exists) {
      document.addEventListener('click', this.handleToggleButtonClick)
    }

    document.addEventListener('pointermove', this.onPointerMove)
    document.addEventListener('mouseleave', this.onMouseLeave)

    document.addEventListener('focusin', this.handleFocusIn)

    document.addEventListener('keyup', this.handleEscapePress)
  }

  removeDesktopListeners () {
    document.removeEventListener('click', this.handleToggleButtonClick)
    document.removeEventListener('pointermove', this.onPointerMove)
    document.removeEventListener('mouseleave', this.onMouseLeave)
    document.removeEventListener('focusin', this.handleFocusIn)
    document.removeEventListener('keyup', this.handleEscapePress)
  }

  onViewportChange (event) {
    const newIsDesktop = event.matches

    if (newIsDesktop && !this.isDesktopMode) {
      this.isDesktopMode = true

      if (!dropdownLoaded) {
        this.connectedCallback()
      } else {
        this.initDesktopListeners()
      }
    } else if (!newIsDesktop && this.isDesktopMode) {
      this.isDesktopMode = false
      this.removeDesktopListeners()
      this.closeDropdowns()
    }
  }

  /**
   * Keyboard path: explicit click-toggle on the invisible-until-focused
   * hd-Banner_Tabbing button next to each hover-triggered link.
   *
   * @param {ClickEvent} event
   *
   * @returns {void}
   */
  handleToggleButtonClick (event) {
    const menuToggleEl = event.target.closest(
      this.els?.['menu-dropdown-toggle']?.selector,
    )
    if (!menuToggleEl) return

    const parentItemEl = menuToggleEl.closest(this.els?.item?.selector)
    if (!parentItemEl) return

    const menuDropdownEl = getElement(this.els?.['menu-dropdown']?.selector, {
      context: parentItemEl,
    })
    if (!menuDropdownEl) return

    const isExpanded = menuToggleEl.getAttribute('aria-expanded') === 'true'

    menuToggleEl.setAttribute('aria-expanded', String(!isExpanded))
    menuDropdownEl.setAttribute('aria-hidden', String(isExpanded))
    this.setBackdrop(!isExpanded)
  }

  /**
   * Mouse path: hovering a trigger link opens its matching dropdown.
   * Tracked via pointermove (not :hover) so leaving one trigger for another
   * swaps the open panel cleanly instead of both being "hovered" at once.
   *
   * @param {PointerEvent} event
   *
   * @returns {void}
   */
  onPointerMove (event) {
    const activeTrigger = event.target.closest(this.els?.trigger?.selector)
    const activeDropdown = event.target.closest(
      this.els?.['menu-dropdown']?.selector,
    )

    if (!activeTrigger && !activeDropdown) {
      this.closeDropdowns()

      return
    }

    const matchedDropdown =
      activeDropdown ||
      getElement(this.els?.['menu-dropdown']?.selector, {
        context: activeTrigger.closest(this.els?.item?.selector),
      })

    if (!matchedDropdown) {
      this.closeDropdowns()

      return
    }

    this.els?.['menu-dropdown']?.elements.forEach((menuDropdownEl) => {
      menuDropdownEl.setAttribute(
        'aria-hidden',
        menuDropdownEl !== matchedDropdown,
      )
    })

    this.els?.['menu-dropdown-toggle']?.elements.forEach((menuToggleEl) => {
      menuToggleEl.setAttribute(
        'aria-expanded',
        menuToggleEl.dataset.id === matchedDropdown.id,
      )
    })

    this.setBackdrop(true)
  }

  onMouseLeave () {
    this.closeDropdowns()
  }

  /**
   * @param {FocusInEvent} event
   *
   * @returns {void}
   */
  handleFocusIn (event) {
    if (
      event.target.closest(this.localName) &&
      !event.target.closest('[aria-hidden="false"]')
    ) {
      /* Closes an expanded dropdown when the user tabs out of it but focus remains within the header-dropdown element */
      this.closeDropdowns()
    }

    if (!event.target.closest(this.localName)) {
      /* Closes all dropdowns when focus leaves the header-dropdown element */
      this.closeDropdowns()
    }
  }

  /**
   * @param {KeyboardEvent} event
   *
   * @returns {void}
   */
  handleEscapePress (event) {
    if (event.code?.toUpperCase() !== 'ESCAPE') return
    this.closeDropdowns()
  }

  /**
   * @returns {void}
   */
  closeDropdowns () {
    if (this.els?.['menu-dropdown-toggle']?.exists) {
      this.els['menu-dropdown-toggle'].elements.forEach((menuToggleEl) => {
        menuToggleEl.setAttribute('aria-expanded', 'false')
      })
    }

    this.els?.['menu-dropdown']?.elements.forEach((menuDropdownEl) => {
      menuDropdownEl.setAttribute('aria-hidden', 'true')
    })

    this.setBackdrop(false)
  }

  /**
   * @param {boolean} isOpen
   *
   * @returns {void}
   */
  setBackdrop (isOpen) {
    const backdropEl = this.externalEls?.backdrop?.element
    if (!backdropEl) return

    backdropEl.setAttribute('aria-hidden', String(!isOpen))
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    this.removeDesktopListeners()
  }
}

if (!customElements.get('header-dropdown')) {
  customElements.define('header-dropdown', HeaderDropdown)
}
